import type { useGT } from 'gt-react';

type Translate = ReturnType<typeof useGT>;

/**
 * What to call one of a pricelist's tables.
 *
 * A spreadsheet's tables are its tabs and arrive under the names they were
 * uploaded with, so those are what the tabs here say. Anything the extraction
 * found in a document rather than being handed under a name — a PDF's tables,
 * a CSV — has only its position to be called by.
 *
 * Shared by every surface that puts tables in tabs (the editor, its windowed
 * form for large pricelists, and the comparison panel), so one pricelist's
 * tables are named the same wherever they are shown.
 *
 * Positional arguments, against the usual rule for three of them: gt-react's
 * extractor only finds the `gt` call below while `gt` is a plain parameter.
 * Take the arguments as an object and "Table {number}" drops out of the
 * generated catalog — `i18n:check` fails, and every locale but English would
 * fall through to the untranslated string.
 */
export function pricelistTableLabel(
  gt: Translate,
  name: string | null | undefined,
  index: number,
): string {
  if (name != null && name.trim() !== '') return name;
  return gt('Table {number}', {
    context: 'tab label for an extracted pricelist table that has no name',
    number: String(index + 1),
  });
}

/**
 * A React key for one of a pricelist's tables, stable across a refetch.
 *
 * Whichever of the two things distinguishes it: a named table by its name,
 * since a workbook cannot hold two tabs called the same; an unnamed one by its
 * headers, since blocks that share headers and have no name were merged into
 * one table before they ever got here. The prefixes keep the two spaces apart,
 * so a name can never collide with some other table's joined headers.
 *
 * Not the index: a key taken from the position is the position, so removing a
 * table would hand its state to whichever one moved up into its place.
 */
export function pricelistTableKey(
  name: string | null | undefined,
  headers: string[],
): string {
  if (name != null && name.trim() !== '') return `name:${name}`;
  return `headers:${headers.join('\0')}`;
}
